import { useCallback } from 'react';
import { attachedProductsStore$ } from '../stores/attached-products.store';
import { useCartItemReference } from './cart-item-reference/use-cart-item-reference.hook';
import { type CartItemReference, type CartItemReferenceLogic, type CartItemReferenceAction } from '../models';
import { uuidGenerator } from '../utils/uuid-generator';

export interface UseAttachedCartItemReferenceLogics {
    getAttachedCartItemReferenceLogics: () => CartItemReferenceLogic[];
    addAttachedCartItemReferenceLogic: (cartItemReferenceLogic: CartItemReferenceLogic) => void;
    removeAttachedCartItemReferenceLogic: (cartItemReferenceLogicId: string) => void;
    clearAttachedCartItemReferenceLogics: () => void;
    getCartItemReferenceId: () => string | undefined;
    getCartItemReference: () => CartItemReference | undefined;
}

export function useAttachedCartItemReferenceLogics(): UseAttachedCartItemReferenceLogics {
    const { getReferenceById: getReferenceByIdFromHook } = useCartItemReference();
    const addAttachedCartItemReferenceLogicInner = useCallback((cartItemReferenceLogic: CartItemReferenceLogic) => {
        console.log('[useAttachedCartItemReferenceLogics] addAttachedCartItemReferenceLogic called:', {
            cartItemReferenceLogic,
            currentItems: attachedProductsStore$.items.peek(),
        });
        attachedProductsStore$.items.set((prev: CartItemReferenceLogic[]) => {
            // Ensure logic id
            const logicId = cartItemReferenceLogic.id || uuidGenerator.generate();
            // Ensure action ids
            const actions: CartItemReferenceAction[] = (cartItemReferenceLogic.actions || []).map(
                (action: CartItemReferenceAction) => ({
                    ...action,
                    id: action.id || uuidGenerator.generate(),
                    cartItemReferenceLogicId: logicId,
                })
            );
            // ════════════════════════════════════════════════════════════════
            // CASE 1: Same rule + same gift SKU → update actionQty only
            // ════════════════════════════════════════════════════════════════
            const existingExactIndex = prev.findIndex(
                (item: CartItemReferenceLogic) =>
                    item.logicId === cartItemReferenceLogic.logicId &&
                    item.cartItemReferenceId === cartItemReferenceLogic.cartItemReferenceId &&
                    item.actions.some(
                        (action: CartItemReferenceAction) =>
                            action.actionProduct &&
                            actions[0]?.actionProduct &&
                            action.actionProduct.sku === actions[0]?.actionProduct.sku
                    )
            );
            if (existingExactIndex !== -1) {
                const updated = [...prev];
                updated[existingExactIndex] = {
                    ...updated[existingExactIndex],
                    actions: updated[existingExactIndex].actions.map((action: CartItemReferenceAction) => {
                        if (
                            action.actionProduct &&
                            actions[0]?.actionProduct &&
                            action.actionProduct.sku === actions[0]?.actionProduct.sku
                        ) {
                            return {
                                ...action,
                                actionQty: actions[0]?.actionQty ?? 0,
                            };
                        }
                        return action;
                    }),
                };
                return updated;
            }

            // ════════════════════════════════════════════════════════════════
            // CASE 2: Same rule + different gift SKU → gift swap
            // Replace the actions (gift product) but keep allocatedQty unchanged.
            // When swapping gifts the quantity allocation per rule stays the same.
            // ════════════════════════════════════════════════════════════════
            const existingRuleIndex = prev.findIndex(
                (item: CartItemReferenceLogic) =>
                    item.logicId === cartItemReferenceLogic.logicId &&
                    item.cartItemReferenceId === cartItemReferenceLogic.cartItemReferenceId
            );
            if (existingRuleIndex !== -1) {
                const existing = prev[existingRuleIndex];
                const updated = [...prev];
                updated[existingRuleIndex] = {
                    ...cartItemReferenceLogic,
                    id: existing.id, // preserve existing ID
                    allocatedQty: existing.allocatedQty, // keep allocatedQty unchanged
                    actions,
                };
                return updated;
            }

            // CASE 3: New rule → add as new
            return [
                ...prev,
                {
                    ...cartItemReferenceLogic,
                    id: logicId,
                    actions,
                },
            ];
        });
    }, []);

    const removeAttachedCartItemReferenceLogic = useCallback((cartItemReferenceLogicId: string) => {
        attachedProductsStore$.items.set((prev: CartItemReferenceLogic[]) =>
            prev.filter((p: CartItemReferenceLogic | null | undefined) => p?.id !== cartItemReferenceLogicId)
        );
    }, []);

    const clearAttachedCartItemReferenceLogics = useCallback(() => {
        attachedProductsStore$.items.set([]);
    }, []);

    const getCartItemReferenceInner = () => {
        return getReferenceByIdFromHook(attachedProductsStore$.cartItemReferenceId.peek() ?? '');
    };

    return {
        getAttachedCartItemReferenceLogics: () => {
            const items = attachedProductsStore$.items.peek() ?? [];
            console.log('[useAttachedCartItemReferenceLogics] getAttachedCartItemReferenceLogics:', {
                items,
                itemsCount: items.length,
            });
            return items;
        },
        addAttachedCartItemReferenceLogic: addAttachedCartItemReferenceLogicInner,
        removeAttachedCartItemReferenceLogic,
        clearAttachedCartItemReferenceLogics,
        getCartItemReferenceId: () => attachedProductsStore$.cartItemReferenceId.peek(),
        getCartItemReference: getCartItemReferenceInner,
    };
}
